import 'dayjs/locale/de';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useRef, useState } from 'react';

import { Button, InternalLink } from '~/components/Button';
import Link from '~/components/Link';
import { DEFAULT_REGION, type Region } from '~/utils/region';

dayjs.extend(utc);
dayjs.extend(timezone);

function formatDate(unixTimestamp: number, region: Region = DEFAULT_REGION) {
  return dayjs(unixTimestamp * 1000)
    .tz(region.timeZone)
    .locale(region.lang)
    .format('DD MMM YY h:mmA');
}

type Author = {
  name: string;
  path: string;
  title: string;
};

type Tag = {
  label: string;
  path: string;
};

type Props = {
  author: Author;
  modifiedDate?: number;
  publishedDate?: number;
  tags?: Tag[];
  title: string;
};

function Tags({ tags = [] }: { tags: Tag[] }) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      setIsOverflowing(
        containerRef.current.scrollHeight > containerRef.current.clientHeight
      );
    }
  }, [tags]);

  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap justify-center gap-4 text-sm text-grey-hide">
      <div
        ref={containerRef}
        className={`flex flex-wrap justify-center gap-4 ${!expanded ? 'max-h-6 overflow-hidden' : ''}`}
      >
        {tags.map((tag, index) => (
          <React.Fragment key={tag.label}>
            {index > 0 && <span>•</span>}
            <a href={tag.path} className="hover:underline">
              {tag.label}
            </a>
          </React.Fragment>
        ))}
      </div>
      {!expanded && isOverflowing && (
        <Button
          variant="none"
          onClick={() => setExpanded(true)}
          className="font-semibold text-black"
        >
          see more
        </Button>
      )}
    </div>
  );
}

function FeatureMetadata({
  tags = [],
  title,
  author,
  publishedDate,
  modifiedDate
}: Props) {
  const displayDate = modifiedDate || publishedDate;
  const isUpdated = modifiedDate && modifiedDate !== publishedDate;

  return (
    <div className="flex max-w-5xl flex-col gap-8 px-4 text-center">
      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-grey-hide">
        <Link
          to={author.path}
          external
          className="underline hover:text-gray-700"
        >
          {author.name}
        </Link>
        <span>•</span>
        <span>{author.title}</span>
        {displayDate && (
          <>
            <span>•</span>
            <span>
              {isUpdated ? 'Updated ' : ''}
              {formatDate(displayDate)}
            </span>
          </>
        )}
      </div>
      <div className="text-[32px] font-light leading-[48px] tracking-[0.05rem] lg:text-[40px]">
        {title}
      </div>
      <Tags tags={tags} />
    </div>
  );
}

export function Header({
  tags,
  title,
  author,
  publishedDate,
  modifiedDate
}: Props) {
  return (
    <div className="flex w-full justify-center bg-white py-12 text-black">
      <FeatureMetadata
        tags={tags}
        title={title}
        author={author}
        publishedDate={publishedDate}
        modifiedDate={modifiedDate}
      />
    </div>
  );
}

type FooterProps = Props & { variant?: 'dark' | 'light' };

export function Footer({
  tags,
  title,
  author,
  publishedDate,
  modifiedDate,
  variant = 'dark'
}: FooterProps) {
  const isDark = variant === 'dark';

  return (
    <div
      className={`flex w-full flex-col items-center gap-6 py-10 md:pb-14 md:pt-16 ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`}
    >
      <FeatureMetadata
        tags={tags}
        title={title}
        author={author}
        publishedDate={publishedDate}
        modifiedDate={modifiedDate}
      />
      <InternalLink
        to="#top"
        variant="none"
        className={`mx-auto rounded-lg border-2 px-[10px] py-2 text-sm font-bold ${isDark ? 'bg-black text-white' : 'border-black bg-white text-black'}`}
      >
        Back to top
      </InternalLink>
    </div>
  );
}
